import { HamburgerIcon, InfoOutlineIcon } from "@chakra-ui/icons"
import {
  Badge,
  Button,
  Checkbox,
  HStack,
  IconButton,
  Input,
  Tab,
  TabList,
  Table,
  TableContainer,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useDeferredValue, useMemo, useState } from "react"
import Select from "@/components/Common/AppSelect"
import { InventoryCountApi } from "../../../../api/inventoryCount"
import { UsersService, WareHouseService } from "../../../../client"
import { WmsListToolbar } from "../../../../components/WMS/shared/WmsListToolbar"
import { WmsLocationFilterInput } from "../../../../components/WMS/shared/WmsLocationFilterInput"
import { WmsPageHeader } from "../../../../components/WMS/shared/WmsPageHeader"
import {
  WmsPagePanel,
  WmsPageShell,
} from "../../../../components/WMS/shared/WmsPageShell"
import { WmsTableListCard } from "../../../../components/WMS/shared/WmsTableListCard"

export const Route = createFileRoute("/_layout/WMS/inventory-count/review")({
  component: InventoryCountReviewQueuePage,
})

type ReviewDecision = "approve" | "recount" | "reject"

function InventoryCountReviewQueuePage() {
  const qc = useQueryClient()
  const [warehouseId, setWarehouseId] = useState("")
  const [assigneeId, setAssigneeId] = useState("")
  const [ageDays, setAgeDays] = useState("")
  const [locationFilter, setLocationFilter] = useState("")
  const deferredLocationFilter = useDeferredValue(locationFilter)
  const [varianceOnly, setVarianceOnly] = useState(false)
  const [reasons, setReasons] = useState<Record<string, string>>({})

  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const theadBg = useColorModeValue("gray.100", "whiteAlpha.100")
  const zebraBg = useColorModeValue("blackAlpha.50", "whiteAlpha.50")
  const mutedText = useColorModeValue("gray.500", "gray.400")

  const warehousesQuery = useQuery({
    queryKey: ["wms", "warehouses", "list", { includeArchived: false }],
    queryFn: () => WareHouseService.getAllWarehouses(false),
    retry: false,
  })

  const usersQuery = useQuery({
    queryKey: ["users", "list", "inventory-count-review"],
    queryFn: () => UsersService.readUsers({ limit: 500, skip: 0 }),
    retry: false,
  })
  const userMetaById = useMemo(() => {
    const map = new Map<string, { acronym: string; fullName: string }>()
    for (const user of usersQuery.data?.data ?? []) {
      const id = String(user.id ?? user.ID ?? "").trim()
      if (!id) {
        continue
      }
      map.set(id, {
        acronym: String(user.acronym ?? "").trim(),
        fullName: String(user.fullName ?? "").trim(),
      })
    }
    return map
  }, [usersQuery.data])

  const tasksQuery = useQuery({
    queryKey: [
      "wms",
      "inventory-count",
      "review-queue",
      {
        warehouseId,
        assigneeId,
        location_code: deferredLocationFilter,
        has_variance: varianceOnly,
      },
    ],
    retry: false,
    queryFn: () =>
      InventoryCountApi.getTasks({
        warehouse_id: warehouseId || undefined,
        status: "pending_review",
        assigned_to:
          assigneeId && assigneeId !== "__unassigned" ? assigneeId : undefined,
        location_code: deferredLocationFilter || undefined,
        has_variance: varianceOnly,
        page: 1,
        page_size: 100,
      }),
  })
  const locksQuery = useQuery({
    queryKey: [
      "wms",
      "inventory-count",
      "locks",
      { warehouseId, location_code: deferredLocationFilter },
    ],
    retry: false,
    queryFn: () =>
      InventoryCountApi.getLocationLocks({
        warehouse_id: warehouseId || undefined,
        location_code: deferredLocationFilter || undefined,
        page: 1,
        page_size: 100,
      }),
  })

  const rows = useMemo(() => {
    const now = Date.now()
    const ageMs =
      Number(ageDays) > 0 ? Number(ageDays) * 24 * 60 * 60 * 1000 : 0

    return (tasksQuery.data?.items ?? []).filter((task) => {
      if (assigneeId === "__unassigned" && task.assigned_to_user_id) {
        return false
      }
      if (ageMs > 0) {
        const taskWiek = now - new Date(task.created_at).getTime()
        if (!Number.isFinite(taskWiek) || taskWiek < ageMs) {
          return false
        }
      }
      return true
    })
  }, [ageDays, assigneeId, tasksQuery.data?.items])

  const filteredLocks = locksQuery.data?.items ?? []

  const reviewMutation = useMutation({
    mutationFn: ({
      taskId,
      decision,
      reason,
    }: {
      taskId: string
      decision: ReviewDecision
      reason?: string
    }) => {
      if (decision === "recount") {
        return InventoryCountApi.recountTask(taskId, {
          reason: reason || undefined,
        })
      }
      return InventoryCountApi.approveTask(taskId, {
        decision,
        reason: reason || undefined,
      })
    },
    onSuccess: async (_data, variables) => {
      setReasons((prev) => ({
        ...prev,
        [variables.taskId]: "",
      }))
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "review-queue"],
      })
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "tasks"],
      })
      await qc.invalidateQueries({
        queryKey: [
          "wms",
          "inventory-count",
          "tasks",
          "detail",
          variables.taskId,
        ],
      })
    },
  })

  const isLoading = tasksQuery.isPending
  const isFetching = tasksQuery.isFetching
  const isError = tasksQuery.isError
  const queryError = tasksQuery.error

  return (
    <WmsPageShell>
      <WmsPageHeader
        title="Kolejka weryfikacji inwentaryzacji"
        description="Kolejka zadań oczekujących na weryfikację z szybką decyzją: zatwierdź / przelicz / odrzuć."
        gradient="linear(to-r, teal.600, blue.700)"
        badge={`Oczekujące na weryfikację: ${rows.length}`}
      />

      <WmsPagePanel bg={panelBg} borderColor={panelBorder}>
        <WmsListToolbar
          actionsWrap="nowrap"
          filters={
            <>
              <Select
                size="sm"
                maxW={{ base: "100%", lg: "240px" }}
                value={warehouseId}
                onChange={(event) => setWarehouseId(event.target.value)}
                borderRadius="xl"
              >
                <option value="">Wszystkie magazyny</option>
                {(warehousesQuery.data ?? []).map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.code} - {warehouse.name}
                  </option>
                ))}
              </Select>
              <Select
                size="sm"
                maxW={{ base: "100%", lg: "260px" }}
                value={assigneeId}
                onChange={(event) => setAssigneeId(event.target.value)}
                borderRadius="xl"
              >
                <option value="">Wszyscy przypisani</option>
                <option value="__unassigned">Nieprzypisane</option>
                {(usersQuery.data?.data ?? []).map((user) => (
                  <option key={user.id ?? user.ID} value={user.id ?? user.ID}>
                    {(user.fullName || user.email || user.id || user.ID) ??
                      "Nieznany użytkownik"}
                  </option>
                ))}
              </Select>
              <Select
                size="sm"
                maxW={{ base: "100%", lg: "220px" }}
                value={ageDays}
                onChange={(event) => setAgeDays(event.target.value)}
                borderRadius="xl"
              >
                <option value="">Dowolny wiek</option>
                <option value="1">Wiek 1+ dzień</option>
                <option value="3">Wiek 3+ dni</option>
                <option value="7">Wiek 7+ dni</option>
              </Select>
              <Checkbox
                size="sm"
                isChecked={varianceOnly}
                onChange={(event) => setVarianceOnly(event.target.checked)}
                whiteSpace="nowrap"
              >
                Tylko z odchyleniem
              </Checkbox>
              <WmsLocationFilterInput
                value={locationFilter}
                onChange={setLocationFilter}
              />
            </>
          }
          actions={
            <HStack spacing={2} wrap="nowrap">
              <Tooltip label="Otwórz kolejkę zadań" hasArrow placement="top">
                <IconButton
                  size="sm"
                  as={Link}
                  to="/WMS/inventory-count/tasks"
                  aria-label="Otwórz kolejkę zadań"
                  icon={<HamburgerIcon />}
                  variant="outline"
                />
              </Tooltip>
              <Tooltip label="Instrukcja pracownika." hasArrow placement="top">
                <IconButton
                  size="sm"
                  as={Link}
                  to="/WMS/inventory-count/instructions-worker"
                  aria-label="Instrukcja pracownika"
                  icon={<InfoOutlineIcon />}
                  variant="outline"
                />
              </Tooltip>
            </HStack>
          }
        />

        <Tabs
          variant="enclosed"
          colorScheme="blue"
          display="flex"
          flexDirection="column"
          flex="1"
          minH={0}
          mt={4}
        >
          <TabList px={4}>
            <Tab>Oczekujące ({rows.length})</Tab>
            <Tab>
              Aktywne blokady lokacji
              <Badge ml={2} colorScheme="purple">
                {filteredLocks.length}
              </Badge>
            </Tab>
          </TabList>

          <TabPanels flex="1" display="flex" flexDirection="column" minH={0}>
            <TabPanel
              flex="1"
              display="flex"
              flexDirection="column"
              p={4}
              minH={0}
            >
              <WmsTableListCard
                isLoading={isLoading}
                isFetching={isFetching}
                error={isError ? queryError : undefined}
                errorMessage="Nie udało się pobrać kolejki weryfikacji inwentaryzacji."
                isEmpty={!isLoading && !isError && rows.length === 0}
                emptyMessage="Brak zadań oczekujących na weryfikację dla wybranych filtrów."
                minEmptyHeight={{ base: "200px", lg: "320px" }}
              >
                <TableContainer maxH="62vh" overflowY="auto">
                  <Table size="sm" sx={{ tableLayout: "fixed" }}>
                    <Thead bg={theadBg} position="sticky" top={0} zIndex={1}>
                      <Tr>
                        <Th w="180px">Nr zadania</Th>
                        <Th w="160px">Przypisany</Th>
                        <Th w="150px">Wiek</Th>
                        <Th w="200px">Utworzono</Th>
                        <Th w="260px">Powód</Th>
                        <Th w="420px">Akcje</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {rows.map((row, idx) => {
                        const reason = reasons[row.id] ?? ""
                        const isReasonRequired = reason.trim().length === 0
                        const ageHours = Math.max(
                          0,
                          Math.floor(
                            (Date.now() - new Date(row.created_at).getTime()) /
                              3600000,
                          ),
                        )
                        return (
                          <Tr
                            key={row.id}
                            bg={idx % 2 === 0 ? "transparent" : zebraBg}
                          >
                            <Td>
                              <HStack spacing={2}>
                                <Text>{row.task_no}</Text>
                                <Badge colorScheme="orange">{row.status}</Badge>
                              </HStack>
                            </Td>
                            <Td>
                              {row.assigned_to_user_id ? (
                                row.assigned_to_user_id
                              ) : (
                                <Text color={mutedText}>—</Text>
                              )}
                            </Td>
                            <Td>{`${ageHours}h`}</Td>
                            <Td>{new Date(row.created_at).toLocaleString()}</Td>
                            <Td>
                              <Input
                                size="xs"
                                value={reason}
                                placeholder="Powód przeliczenia/odrzucenia"
                                onChange={(event) =>
                                  setReasons((prev) => ({
                                    ...prev,
                                    [row.id]: event.target.value,
                                  }))
                                }
                              />
                            </Td>
                            <Td>
                              <HStack spacing={2} wrap="wrap">
                                <Button
                                  as="a"
                                  href={`/WMS/inventory-count/tasks/${row.id}`}
                                  size="xs"
                                  variant="outline"
                                >
                                  Otwórz szczegóły
                                </Button>
                                <Button
                                  size="xs"
                                  colorScheme="green"
                                  isLoading={
                                    reviewMutation.isPending &&
                                    reviewMutation.variables?.taskId ===
                                      row.id &&
                                    reviewMutation.variables?.decision ===
                                      "approve"
                                  }
                                  onClick={() =>
                                    reviewMutation.mutate({
                                      taskId: row.id,
                                      decision: "approve",
                                      reason: reason.trim() || undefined,
                                    })
                                  }
                                >
                                  Zatwierdź
                                </Button>
                                <Button
                                  size="xs"
                                  colorScheme="orange"
                                  isDisabled={isReasonRequired}
                                  isLoading={
                                    reviewMutation.isPending &&
                                    reviewMutation.variables?.taskId ===
                                      row.id &&
                                    reviewMutation.variables?.decision ===
                                      "recount"
                                  }
                                  onClick={() =>
                                    reviewMutation.mutate({
                                      taskId: row.id,
                                      decision: "recount",
                                      reason: reason.trim(),
                                    })
                                  }
                                >
                                  Przelicz
                                </Button>
                                <Button
                                  size="xs"
                                  colorScheme="red"
                                  variant="outline"
                                  isDisabled={isReasonRequired}
                                  isLoading={
                                    reviewMutation.isPending &&
                                    reviewMutation.variables?.taskId ===
                                      row.id &&
                                    reviewMutation.variables?.decision ===
                                      "reject"
                                  }
                                  onClick={() =>
                                    reviewMutation.mutate({
                                      taskId: row.id,
                                      decision: "reject",
                                      reason: reason.trim(),
                                    })
                                  }
                                >
                                  Odrzuć
                                </Button>
                              </HStack>
                            </Td>
                          </Tr>
                        )
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
              </WmsTableListCard>
            </TabPanel>

            <TabPanel
              flex="1"
              display="flex"
              flexDirection="column"
              p={4}
              minH={0}
            >
              <WmsTableListCard
                isLoading={locksQuery.isPending}
                isFetching={locksQuery.isFetching}
                error={locksQuery.isError ? locksQuery.error : undefined}
                errorMessage="Nie udało się pobrać aktywnych blokad lokacji."
                isEmpty={
                  !locksQuery.isPending &&
                  !locksQuery.isError &&
                  filteredLocks.length === 0
                }
                emptyMessage="Brak aktywnych blokad lokacji."
                minEmptyHeight={{ base: "140px", lg: "180px" }}
              >
                <TableContainer maxH="62vh" overflowY="auto">
                  <Table size="sm">
                    <Thead bg={theadBg} position="sticky" top={0} zIndex={1}>
                      <Tr>
                        <Th>Nr zadania</Th>
                        <Th>Status</Th>
                        <Th>Lokacja</Th>
                        <Th>Zablokowane przez</Th>
                        <Th>Zablokowano</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredLocks.map((lock, idx) => (
                        <Tr
                          key={lock.id}
                          bg={idx % 2 === 0 ? "transparent" : zebraBg}
                        >
                          <Td>{lock.task_no}</Td>
                          <Td>
                            <Badge
                              colorScheme={
                                lock.task_status === "in_progress"
                                  ? "blue"
                                  : "orange"
                              }
                            >
                              {lock.task_status}
                            </Badge>
                          </Td>
                          <Td>{lock.location_code}</Td>
                          <Td>
                            {(() => {
                              const meta = userMetaById.get(lock.locked_by_id)
                              if (!meta) return lock.locked_by_id
                              if (meta.acronym) return meta.acronym
                              if (meta.fullName) return meta.fullName
                              return lock.locked_by_id
                            })()}
                          </Td>
                          <Td>{new Date(lock.locked_at).toLocaleString()}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </WmsTableListCard>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </WmsPagePanel>
    </WmsPageShell>
  )
}
